/**
 * languagesList.tsx
 */
import ArrowRightIcon from '@components/atoms/icons/arrowRight';
import Colors from '@shared/assets/style/default/color';
import Direction from '@shared/types/enums/direction';
import Focus from '@shared/types/enums/focus';
import LanguageItem from '@components/elements/language/languageItem';
import LanguageListArrowContainer from '@components/atoms/container/languageListArrowContainer';
import LanguageListContainer from '@components/atoms/container/languageListContainer';
import LanguageListWrapper from '@components/atoms/container/languageListWrapper';
import List from '../list';
import TranslationManager from '@shared/assets/locale/translationsManager';
import initCategories from '@shared/actions/categories/initCategories';
import keepWatchingRequest from '@shared/api/initial/keepWatchingRequest';
import useTransform from '@shared/components/utils/hooks/useTransform';
import { LanguageListProps as Props } from '@shared/types/props/language';
import { selectFocus, pushFocus } from '@shared/store/slices/focus.slice';
import { selectMagicRemoteEnabled } from '@shared/store/slices/magicRemote.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useRef } from 'react';

/**
 * Constants
 * - LANGUAGE_DISPLACEMENT: The displacement of the language list.
 */
const LANGUAGE_DISPLACEMENT = 62;

/**
 * Displays a list of languages to choose from.
 */
export default function LanguagesList({ animate = true, languages }: Props) {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  const languageIndex = languages.findIndex((item) => {
    return TranslationManager.currentLanguage === item;
  });

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const focus = useAppSelector(selectFocus);
  const enabled = useAppSelector(selectMagicRemoteEnabled);

  // Refs
  const focusRef = useRef(focus);
  const originalLanguage = useRef(TranslationManager.currentLanguage);
  focusRef.current = focus;

  // Transform
  const [style, setStyle] = useTransform(
    languageIndex,
    LANGUAGE_DISPLACEMENT,
    Direction.VERTICAL
  );

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onChange = (index: number, increment: number | undefined) => {
    if (increment) {
      setStyle(increment);
      TranslationManager.setLanguage(languages[index]);
    }
  };

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    focusRef.current !== Focus.LANGUAGES &&
      dispatch(pushFocus(Focus.LANGUAGES));
    return () => {
      // When app language, categories must be fetched once again with the new language
      if (originalLanguage.current !== TranslationManager.currentLanguage) {
        initCategories();
        keepWatchingRequest();
      }
    };
  }, []);

  // ------------------------------------------------------------
  // Return values
  // ------------------------------------------------------------

  return (
    <LanguageListContainer animate={animate} id={'languageListContainer'}>
      <LanguageListWrapper
        style={animate ? style : undefined}
        id={'languageListWrapper'}
      >
        <List
          direction={Direction.VERTICAL}
          focus={Focus.LANGUAGES}
          initialIndex={languageIndex}
          onChange={onChange}
        >
          {languages.map((item, index) => {
            return <LanguageItem index={index} key={index} item={item} />;
          })}
        </List>
      </LanguageListWrapper>
      {!enabled && (
        <LanguageListArrowContainer
          offset={animate ? 0 : LANGUAGE_DISPLACEMENT * languageIndex}
          id={'arrowContainer'}
        >
          <ArrowRightIcon color={Colors.selector} width={18} />
        </LanguageListArrowContainer>
      )}
    </LanguageListContainer>
  );
}
